import { DownloadOutlined, FileOutlined, PictureOutlined } from '@ant-design/icons'
import { Button, Image, message, Space } from 'antd'
import { FC, MouseEvent, useState } from 'react'
import downloadFileByBlob from '../../helpers/download-file-by-blob'
import isEmbeddableImageUrl from '../../helpers/is-embeddable-image-url'
import useErrorModal from '../../hooks/api/use-error-modal'
import { useLazyDownloadUploadedFileQuery } from '../../store/api/upload-api'
import Stamp from '../../types/api/stamp'
import getStampPreview from './get-stamp-preview'

interface StampImageLinkProps {
    stamp: Pick<Stamp, 'image' | 'imageLink' | 'layoutLink'>
    download?: boolean
    layout?: boolean
}

const iconStyle = { cursor: 'pointer', fontSize: 16 }
const hiddenImageStyle = { display: 'none' }

const StampImageLink: FC<StampImageLinkProps> = ({ stamp, download = false, layout = false }) => {
    const preview = getStampPreview(stamp)
    const layoutLink = stamp.layoutLink?.trim()
    const [previewVisible, setPreviewVisible] = useState(false)
    const [downloadUploadedFile, downloadState] = useLazyDownloadUploadedFileQuery()

    useErrorModal(downloadState, 'Не удалось скачать превью штампа')

    if (!preview && !(layout && layoutLink)) {
        return null
    }

    const onDownload = async (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        const file = preview?.file
        if (!file?.url) return

        const downloadRes = await downloadUploadedFile(file.url)
        if ('error' in downloadRes && downloadRes.error) return
        if (!downloadRes.data) {
            message.error('Не удалось скачать превью штампа')
            return
        }

        downloadFileByBlob(downloadRes.data, file.name)
    }

    const embeddable = preview ? isEmbeddableImageUrl(preview.url) : false

    return (
        <Space size={4} align={'center'} onClick={event => event.stopPropagation()}>
            {preview && !embeddable ? (
                <a
                    href={preview.url}
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    aria-label={'Открыть изображение штампа'}
                    title={'Открыть превью'}
                >
                    <PictureOutlined style={iconStyle} />
                </a>
            ) : null}
            {preview && embeddable ? (
                <>
                    <PictureOutlined
                        role={'button'}
                        aria-label={'Превью изображения штампа'}
                        title={'Превью'}
                        onClick={() => setPreviewVisible(true)}
                        style={iconStyle}
                    />
                    <Image
                        style={hiddenImageStyle}
                        src={preview.url}
                        preview={{
                            visible: previewVisible,
                            onVisibleChange: setPreviewVisible,
                        }}
                    />
                </>
            ) : null}
            {download && preview?.file ? (
                <Button
                    size={'small'}
                    icon={<DownloadOutlined />}
                    title={'Скачать превью'}
                    aria-label={'Скачать превью штампа'}
                    loading={downloadState.isLoading || downloadState.isFetching}
                    onClick={onDownload}
                />
            ) : null}
            {layout && layoutLink ? (
                <a
                    href={layoutLink}
                    target={'_blank'}
                    rel={'noopener noreferrer'}
                    aria-label={'Открыть макет штампа'}
                    title={'Макет'}
                >
                    <FileOutlined style={iconStyle} />
                </a>
            ) : null}
        </Space>
    )
}

export default StampImageLink
